import React, { useState, useEffect } from 'react';
import { Plus, FileText, Lock, ArrowRight, Calendar as CalendarIcon, Check } from 'lucide-react';
import { useCalendarStore } from '../../../stores/calendarStore';
import { useContentPlanStore } from '../../../stores/contentPlanStore';
import { useUserStore } from '../../../stores/userStore';
import { Event, CalendarView } from '../../../types/calendar';
import CalendarHeader from './CalendarHeader';
import CalendarGrid from './CalendarGrid';
import EventModal from './EventModal';
import EventDetailsModal from './EventDetailsModal';
import DayEventsModal from './DayEventsModal';
import ImportPlanModal from './ImportPlanModal';
import { useLocation, useNavigate } from 'react-router-dom';

export default function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>('month');
  const [showEventModal, setShowEventModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDayEventsModal, setShowDayEventsModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState<Event[]>([]);

  const { events, getEventsByDate } = useCalendarStore();
  const { plans } = useContentPlanStore();
  const { isPremium, isPro } = useUserStore();
  const location = useLocation();
  const navigate = useNavigate();

  // Check location state for import modal and plan selection
  useEffect(() => {
    const state = location.state as { showImportModal?: boolean; selectedPlanId?: string } | null;
    if (state?.showImportModal) {
      if (isPremium() || isPro()) {
        setShowImportModal(true);
      } else {
        navigate('/dashboard/precos');
      }
      // Clear the state after handling
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate, isPremium, isPro]);

  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleSlotClick = (date: Date) => {
    const dayEvents = getEventsByDate(date);
    if (dayEvents.length > 0) {
      setSelectedDate(date);
      setSelectedDayEvents(dayEvents);
      setShowDayEventsModal(true);
    } else {
      setSelectedDate(date);
      setShowEventModal(true);
    }
  };

  const handleEventClick = (event: Event, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedEvent(event);
    setShowDetailsModal(true);
  };

  const handleEditEvent = () => {
    if (selectedEvent) {
      setShowDetailsModal(false);
      setSelectedDate(new Date(selectedEvent.start));
      setShowEventModal(true);
    }
  };

  // Check if user has access to premium features
  const hasPremiumAccess = isPremium() || isPro();

  return (
    <div className="space-y-8">
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
              <CalendarIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Calendário de Conteúdo</h1>
              <p className="text-gray-600">
                {hasPremiumAccess 
                  ? 'Planeje e organize seu conteúdo'
                  : 'Calendário básico para planejamento'}
              </p>
            </div>
          </div>
          {hasPremiumAccess && (
            <div className="flex space-x-3">
              <button
                onClick={() => setShowImportModal(true)}
                className="px-4 py-2 bg-white border border-indigo-200 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors flex items-center transform hover:scale-105"
              >
                <FileText className="h-5 w-5 mr-2" />
                Importar Plano
              </button>
              <button
                onClick={() => {
                  setSelectedDate(new Date());
                  setSelectedEvent(null);
                  setShowEventModal(true);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors transform hover:scale-105"
              >
                <Plus className="h-5 w-5 mr-2" />
                Criar Evento
              </button>
            </div>
          )}
        </div>

        {!hasPremiumAccess && (
          <div className="mb-6 p-6 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-lg relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-indigo-100 rounded-full opacity-30"></div>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-purple-100 rounded-full opacity-30"></div>
            
            <div className="relative flex items-center space-x-4">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                <Lock className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-indigo-900 text-lg">Recursos Premium Disponíveis</h3>
                <p className="text-indigo-700 text-sm mt-1">
                  Atualize para o plano Premium ou Pro para acessar:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 mt-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Check className="h-3 w-3 text-indigo-600" />
                    </div>
                    <span className="text-sm text-indigo-700">Vinculação com ideias e planos</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Check className="h-3 w-3 text-indigo-600" />
                    </div>
                    <span className="text-sm text-indigo-700">Priorização de eventos</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Check className="h-3 w-3 text-indigo-600" />
                    </div>
                    <span className="text-sm text-indigo-700">Status de publicação</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Check className="h-3 w-3 text-indigo-600" />
                    </div>
                    <span className="text-sm text-indigo-700">Eventos recorrentes</span>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/dashboard/precos')}
                  className="mt-4 inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium text-sm"
                >
                  Ver planos disponíveis
                  <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        )}

        <CalendarHeader
          currentDate={currentDate}
          view={view}
          onViewChange={setView}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <CalendarGrid
            currentDate={currentDate}
            view={view}
            events={events}
            onSlotClick={handleSlotClick}
            onEventClick={handleEventClick}
          />
        </div>

        {showEventModal && selectedDate && (
          <EventModal
            date={selectedDate}
            event={selectedEvent}
            hasPremiumAccess={hasPremiumAccess}
            onClose={() => {
              setShowEventModal(false);
              setSelectedEvent(null);
            }}
          />
        )}

        {showDetailsModal && selectedEvent && (
          <EventDetailsModal
            event={selectedEvent}
            hasPremiumAccess={hasPremiumAccess}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedEvent(null);
            }}
            onEdit={handleEditEvent}
          />
        )}

        {showDayEventsModal && selectedDate && (
          <DayEventsModal
            date={selectedDate}
            events={selectedDayEvents}
            onClose={() => {
              setShowDayEventsModal(false);
              setSelectedDate(null);
              setSelectedDayEvents([]);
            }}
            onEventClick={(event) => {
              setShowDayEventsModal(false);
              setSelectedEvent(event);
              setShowDetailsModal(true);
            }}
          />
        )}

        {showImportModal && hasPremiumAccess && (
          <ImportPlanModal
            plans={plans}
            onClose={() => setShowImportModal(false)}
          />
        )}
      </div>
    </div>
  );
}